import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Trophy, Users, Target, CheckCircle, ArrowRight, Phone, Mail } from 'lucide-react';
const Index = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);
    // Observe both old and new scroll reveal elements
    const elements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-up');
    elements.forEach(el => observer.observe(el));

    // Parallax effect for floating elements
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.parallax-element');
      parallaxElements.forEach(element => {
        const speed = 0.3;
        const yPos = -(scrolled * speed);
        (element as HTMLElement).style.transform = `translateY(${yPos}px)`;
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Animated Counter Effect
  useEffect(() => {
    const animateCounters = () => {
      const counters = document.querySelectorAll('.counter');
      counters.forEach(counter => {
        const target = parseInt((counter as HTMLElement).dataset.target || '0');
        const increment = target / 100;
        let current = 0;
        const updateCounter = () => {
          if (current < target) {
            current += increment;
            (counter as HTMLElement).textContent = Math.ceil(current).toString();
            requestAnimationFrame(updateCounter);
          } else {
            (counter as HTMLElement).textContent = target.toString();
          }
        };

        // Start animation when element is visible
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              updateCounter();
              observer.unobserve(entry.target);
            }
          });
        });
        observer.observe(counter);
      });
    };

    // Particle System
    const createParticles = () => {
      const container = document.getElementById('particles');
      if (!container) return;
      for (let i = 0; i < 20; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.width = Math.random() * 4 + 2 + 'px';
        particle.style.height = particle.style.width;
        particle.style.animationDelay = Math.random() * 20 + 's';
        particle.style.animationDuration = Math.random() * 10 + 15 + 's';
        container.appendChild(particle);
      }
    };

    // Magnetic Effect
    const addMagneticEffect = () => {
      const magneticElements = document.querySelectorAll('.magnetic');
      magneticElements.forEach(element => {
        element.addEventListener('mousemove', e => {
          const rect = (element as HTMLElement).getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;
          (element as HTMLElement).style.transform = `translate(${x * 0.1}px, ${y * 0.1}px)`;
        });
        element.addEventListener('mouseleave', () => {
          (element as HTMLElement).style.transform = 'translate(0, 0)';
        });
      });
    };
    animateCounters();
    createParticles();
    addMagneticEffect();
  }, []);
  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Football field background con overlay per leggibilità */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('./images/football_field_background_3.jpeg')`,
        backgroundPosition: 'center center'
      }}></div>
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-black mb-6 animate-fade-in-up leading-tight text-white title-hero">
              Dalla Serie A al tuo futuro nel calcio.
            </h1>
            <p className="text-xl md:text-2xl mb-4 animate-fade-in-up-delay font-medium leading-relaxed stagger-1">
              Scopri se hai davvero il potenziale per arrivare, grazie al metodo e l'esperienza di <span className="text-accent font-bold">Gaby Mudingayi</span>.
            </p>
            <p className="text-lg md:text-xl mb-8 animate-fade-in-up-delay opacity-90 max-w-3xl mx-auto stagger-2">
              Ex giocatore di <span className="text-primary-glow font-semibold">Serie A</span> con oltre <span className="text-accent font-bold">400 presenze</span> tra Inter, Bologna e Lazio, oggi aiuta giovani calciatori e famiglie a costruire un percorso reale verso il professionismo.
            </p>
            <div className="animate-fade-in-up-delay">
              <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary hover:bg-primary/90 h-11 rounded-md btn-liquid magnetic px-8 py-4 text-lg font-bold transition-smooth shadow-glow hover:scale-105 text-[rgb(0,0,0)]" onClick={() => scrollToSection('valutazione')}>
                ✨ Prenota la tua valutazione personale ora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Chi è Gaby Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="scroll-reveal-up card-3d stagger-1">
              <div className="card-3d-inner">
                <div className="photo-container photo-frame relative max-w-md mx-auto lg:mx-0">
                  <img src="./images/gaby_mudingayi_teams_3.jpeg" alt="Gaby Mudingayi - Ex Serie A Player" className="w-full h-auto rounded-2xl transition-transform duration-500 magnetic" />
                  
                  {/* Shine overlay effect */}
                  <div className="photo-overlay"></div>
                  
                  {/* Floating stats that appear on hover */}
                  <div className="floating-stat top-4 left-4">
                    ⚽ 400+ Presenze
                  </div>
                  <div className="floating-stat top-4 right-4">
                    🏆 Serie A
                  </div>
                  <div className="floating-stat bottom-16 left-4">
                    🔥 15 Anni
                  </div>
                  <div className="floating-stat bottom-16 right-4">
                    ⭐ 3 Club
                  </div>
                  
                  {/* Interactive trophy badge */}
                  <div className="absolute -bottom-4 -right-4 interactive-badge bg-gradient-to-br from-accent to-accent/80 text-black p-4 rounded-xl pulsing-glow">
                    <Trophy className="h-8 w-8 float-animation" />
                  </div>
                  
                  {/* Team badges overlay */}
                  <div className="absolute top-2 left-2 flex space-x-1">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg hover:scale-110 transition-transform">
                      I
                    </div>
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg hover:scale-110 transition-transform">
                      B
                    </div>
                    <div className="w-8 h-8 bg-sky-400 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg hover:scale-110 transition-transform">
                      L
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="scroll-reveal-up stagger-2">
              <Badge className="mb-4 badge-premium text-black font-bold interactive-badge">Chi è Gaby Mudingayi</Badge>
              <h2 className="text-4xl font-bold mb-6 text-white title-section text-reveal">
                "Ho vissuto il calcio da dentro, ho giocato con <span className="text-accent glitch" data-text="campioni">campioni</span> e ho imparato cosa serve davvero per arrivare."
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed text-reveal">
                "Oggi voglio mettere la mia <span className="text-accent font-semibold">esperienza</span>, i miei <span className="text-primary font-semibold">contatti</span> e il mio metodo a disposizione di chi sogna di fare il salto."
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center space-x-3 scroll-reveal-up stagger-3 hover:scale-105 transition-transform">
                  <CheckCircle className="h-5 w-5 text-primary pulsing-glow" />
                  <span className="font-semibold text-white">400+ presenze tra Serie A e Belgio</span>
                </div>
                <div className="flex items-center space-x-3 scroll-reveal-up stagger-4 hover:scale-105 transition-transform">
                  <CheckCircle className="h-5 w-5 text-primary pulsing-glow" />
                  <span className="font-semibold text-white">Ex giocatore di Inter, Bologna, Lazio</span>
                </div>
                <div className="flex items-center space-x-3 scroll-reveal-up stagger-1 hover:scale-105 transition-transform">
                  <CheckCircle className="h-5 w-5 text-primary pulsing-glow" />
                  <span className="font-semibold text-white">Consulente sportivo e mentore</span>
                </div>
                <div className="flex items-center space-x-3 scroll-reveal-up stagger-2 hover:scale-105 transition-transform">
                  <CheckCircle className="h-5 w-5 text-primary pulsing-glow" />
                  <span className="font-semibold text-white">Rete diretta con procuratori e club</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Carriera */}
      <section className="py-20 bg-gradient-to-br from-background to-card relative overflow-hidden">
        <div className="aurora"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 scroll-reveal-up">
            <Badge className="mb-4 badge-premium text-black font-bold">La Mia Carriera</Badge>
            <h2 className="text-4xl font-bold mb-4 title-section text-glow">Dal Campo alla Consulenza</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Un percorso di <span className="text-accent font-semibold">eccellenza</span> che oggi metto al servizio dei giovani talenti
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="timeline-item scroll-reveal-up stagger-1 morph-shape">
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-accent">2005-2007 • Lazio</h3>
                    <Trophy className="h-6 w-6 text-accent float-animation" />
                  </div>
                  <p className="text-muted-foreground">Centrocampista titolare in Serie A, esperienza in Champions League e competizioni internazionali.</p>
                </div>
              </div>
              
              <div className="timeline-item scroll-reveal-up stagger-2 morph-shape">
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-accent">2007-2012 • Bologna FC</h3>
                    <Target className="h-6 w-6 text-accent float-animation" />
                  </div>
                  <p className="text-muted-foreground">Ruolo chiave nel centrocampo rossoblù, leadership e esperienza al servizio della squadra.</p>
                </div>
              </div>
              
              <div className="timeline-item scroll-reveal-up stagger-3 morph-shape">
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-accent">2015-2018 • Lazio</h3>
                    <Star className="h-6 w-6 text-accent float-animation" />
                  </div>
                  <p className="text-muted-foreground">Capitano e punto di riferimento, contributo decisivo nelle competizioni europee.</p>
                </div>
              </div>
              
              <div className="timeline-item scroll-reveal-up stagger-4 morph-shape">
                <div className="bg-gradient-to-r from-accent/10 to-primary/10 backdrop-blur-sm rounded-xl p-6 border-2 border-accent/30">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-accent glitch" data-text="2018-Oggi • Consulente Sportivo">2018-Oggi • Consulente Sportivo</h3>
                    <Users className="h-6 w-6 text-accent float-animation" />
                  </div>
                  <p className="text-muted-foreground">Mentore e consulente per giovani talenti, ponte tra il mondo giovanile e quello professionistico.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contatori Animati */}
      <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10 relative">
        <div className="particles-container" id="particles"></div>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center scroll-reveal-up stagger-1">
              <div className="relative">
                <div className="text-4xl font-black text-accent mb-2 counter" data-target="400">0</div>
                <div className="light-ray"></div>
              </div>
              <div className="text-sm text-muted-foreground font-semibold">Presenze Totali</div>
            </div>
            <div className="text-center scroll-reveal-up stagger-2">
              <div className="text-4xl font-black text-primary mb-2 counter" data-target="15">0</div>
              <div className="text-sm text-muted-foreground font-semibold">Anni di Carriera</div>
            </div>
            <div className="text-center scroll-reveal-up stagger-3">
              <div className="text-4xl font-black text-accent mb-2 counter" data-target="50">0</div>
              <div className="text-sm text-muted-foreground font-semibold">Giovani Seguiti</div>
            </div>
            <div className="text-center scroll-reveal-up stagger-4">
              <div className="text-4xl font-black text-primary mb-2 counter" data-target="4">0</div>
              <div className="text-sm text-muted-foreground font-semibold">Club Serie A</div>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi Section */}
      <section id="servizi" className="py-20 relative">
        {/* Background con pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-muted/40 to-muted/20"></div>
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url('./images/football_services_6.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 scroll-reveal-up relative">
            <Badge className="mb-4 badge-premium text-black font-bold">I Miei Servizi</Badge>
            <h2 className="text-4xl font-bold mb-4 title-section text-glow">Percorsi Professionali per il Tuo Futuro</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Scegli il servizio più adatto al tuo livello e ai tuoi obiettivi nel mondo del <span className="text-accent font-semibold">calcio</span>
            </p>
            {/* Decorative line */}
            <div className="mt-8 flex justify-center">
              <div className="w-24 h-1 bg-gradient-to-r from-transparent via-accent to-transparent rounded-full"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Valutazione Professionale */}
            <Card id="valutazione" className="scroll-reveal-up card-hover-lift border-gradient stagger-1 relative decoration-dots">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Target className="h-8 w-8 text-accent float-animation" />
                  <Badge className="badge-premium text-black font-bold">Più Richiesto</Badge>
                </div>
                <CardTitle className="text-xl title-section">Valutazione Professionale del Giocatore</CardTitle>
                <CardDescription className="text-base">
                  Ti valuto personalmente per capire il tuo vero potenziale e se sei pronto per il calcio professionistico.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Analisi tecnica e comportamentale</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Report dettagliato e consigli personalizzati</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Accesso al mio network professionale</span>
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Analisi video (1h)</span>
                    <span className="text-2xl font-bold text-accent">390€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Valutazione dal vivo</span>
                    <span className="text-2xl font-bold text-accent">590€ + spese</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full btn-premium text-accent-foreground hover:opacity-90 transition-smooth font-bold"
                  onClick={() => window.open('https://calendly.com/gabymudingayiconsulting', '_blank')}
                >
                  ⚽ Richiedi la tua valutazione ora
                </Button>
              </CardContent>
            </Card>

            {/* Provini */}
            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-2 relative">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Trophy className="h-8 w-8 text-accent float-animation" />
                  <Badge className="badge-premium text-black font-bold">Esclusivo</Badge>
                </div>
                <CardTitle className="text-xl title-section text-glow">Provini in Club Professionali</CardTitle>
                <CardDescription className="text-base">
                  Accesso diretto ai club italiani (Serie A - Serie C) attraverso la mia rete di contatti.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Organizzazione provino ufficiale</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Coaching e supporto pre-provino</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Analisi post-esperienza</span>
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">1 provino </span>
                    <span className="text-2xl font-bold text-accent">1.490€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">3 provini + accompagnamento</span>
                    <span className="text-2xl font-bold text-accent">3.500€</span>
                  </div>
                </div>
                
                <Button 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary hover:bg-primary/90 h-10 px-4 py-2 w-full btn-exclusive hover:opacity-90 transition-smooth font-bold text-[rgb(0,0,0)]"
                  onClick={() => window.open('https://calendly.com/gabymudingayiconsulting', '_blank')}
                >
                  🏆 Richiedi il tuo primo provino
                </Button>
              </CardContent>
            </Card>

            {/* Allenamenti */}
            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-3 relative decoration-dots">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Users className="h-8 w-8 text-accent float-animation" />
                  <Badge className="badge-premium text-black font-bold">Flessibile</Badge>
                </div>
                <CardTitle className="text-xl title-section">Allenamenti Individuali o di Gruppo</CardTitle>
                <CardDescription className="text-base">
                  Allenati come un professionista con le metodologie che mi hanno portato in Serie A.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Allenamento personalizzato "GM Method"</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Kit da allenamento brandizzato</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span className="text-sm">Analisi video e feedback tecnico</span>
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Sessione individuale</span>
                    <span className="text-2xl font-bold text-accent">80€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">5 sessioni</span>
                    <span className="text-2xl font-bold text-accent">350€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Sessione di gruppo</span>
                    <span className="text-2xl font-bold text-accent">60€/persona</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full btn-premium text-accent-foreground hover:opacity-90 transition-smooth font-bold"
                  onClick={() => window.open('https://calendly.com/gabymudingayiconsulting', '_blank')}
                >
                  💪 Prenota il tuo allenamento
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Elite Player Program - Full Width */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-4 relative parallax-container shimmer">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Star className="h-12 w-12 text-accent float-animation text-glow" />
                </div>
                <Badge className="mb-4 badge-premium text-black border-2 border-white text-lg px-6 py-3 font-black shadow-lg animate-pulse-glow">PROGRAMMA ELITE</Badge>
                <CardTitle className="text-3xl font-bold title-section text-glow">Percorso Annuale "ELITE PLAYER"</CardTitle>
                <CardDescription className="text-lg">
                  Il programma più esclusivo: Gaby segue il giocatore per un anno intero insieme al suo team.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 text-lg">Cosa include:</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Visione e analisi partite</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Coaching personalizzato</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Gestione provini e opportunità in club</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Supporto per sponsor tecnici</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Allenamenti estivi con tecnico professionista</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-accent" />
                        <span>Consulenza motivazionale e pianificazione carriera</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="bg-gradient-to-br from-card to-card/80 rounded-2xl p-8 shadow-lg border border-accent/30">
                      <div className="text-4xl font-black text-accent mb-2">9.900€</div>
                      <div className="text-lg text-white mb-4">/anno</div>
                      <div className="text-sm text-white/80 mb-6">Oppure 825€ al mese</div>
                      <Button 
                        size="lg" 
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary hover:bg-primary/90 h-11 rounded-md px-8 w-full btn-exclusive hover:opacity-90 transition-smooth text-lg py-3 font-bold animate-pulse-glow text-[rgb(0,0,0)]"
                        onClick={() => window.open('https://calendly.com/gabymudingayiconsulting', '_blank')}
                      >
                        🌟 Candidati al programma annuale
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonianze */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-reveal-up">
            <Badge className="mb-4 badge-premium text-black font-bold">Testimonianze</Badge>
            <h2 className="text-4xl font-bold mb-4 title-section text-glow">Storie di Successo</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Scopri come ho aiutato giovani talenti a realizzare i loro sogni nel <span className="text-accent font-semibold">calcio</span>
            </p>
            {/* Decorative line */}
            <div className="mt-8 flex justify-center">
              <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary to-transparent rounded-full"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-1">
              <CardContent className="p-6 relative">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-lg mb-4 italic">
                  "Gaby ha visto in me cose che non avevo mai notato. Dopo 2 mesi mi ha portato a fare un provino in Serie C."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="font-bold text-primary">L</span>
                  </div>
                  <div>
                    <div className="font-semibold">Luca</div>
                    <div className="text-sm text-muted-foreground">17 anni</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-2">
              <CardContent className="p-6 relative">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-lg mb-4 italic">
                  "Mio figlio era demotivato. Con Gaby ha ritrovato la passione e oggi è seguito da uno sponsor tecnico."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="font-bold text-primary">D</span>
                  </div>
                  <div>
                    <div className="font-semibold">Davide</div>
                    <div className="text-sm text-muted-foreground">Papà di un ragazzo 2008</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="scroll-reveal-up card-hover-lift border-gradient stagger-3">
              <CardContent className="p-6 relative">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-lg mb-4 italic">
                  "Il metodo di Gaby è unico. Mi ha fatto capire cosa significa davvero essere un professionista."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="font-bold text-primary">M</span>
                  </div>
                  <div>
                    <div className="font-semibold">Marco</div>
                    <div className="text-sm text-muted-foreground">19 anni</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Come Funziona */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-reveal-up">
            <Badge className="mb-4 badge-premium text-black font-bold">Come Funziona</Badge>
            <h2 className="text-4xl font-bold mb-4 title-section text-glow">Il Tuo Percorso in 3 Semplici Passi</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center scroll-reveal-up stagger-1">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-glow text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-glow float-animation">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2 title-section">Prenota la tua valutazione personale</h3>
              <p className="text-muted-foreground">Scegli il servizio più adatto alle tue esigenze e prenota online</p>
            </div>
            
            <div className="text-center scroll-reveal-up stagger-2">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-glow text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-glow float-animation">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2 title-section">Ti contattiamo entro 24h</h3>
              <p className="text-muted-foreground">Il mio team ti contatterà per organizzare tutto nei dettagli</p>
            </div>
            
            <div className="text-center scroll-reveal-up stagger-3">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-glow text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-glow float-animation">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2 title-section">Decidiamo insieme il percorso giusto per te</h3>
              <p className="text-muted-foreground">Creiamo un piano personalizzato per il tuo futuro nel <span className="text-muted-foreground font-semibold">calcio</span></p>
            </div>
          </div>

          <div className="text-center mt-16 scroll-reveal">
            <div className="relative bg-gradient-to-br from-card to-card/80 rounded-2xl p-8 max-w-2xl mx-auto shadow-elegant overflow-hidden border border-primary/20">
              <div className="absolute inset-0 opacity-10">
                <img src="./images/football_services_2.jpeg" alt="Background" className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl"></div>
              <div className="relative z-10">
                <blockquote className="text-2xl font-bold text-white mb-4">
                  "Non vendo illusioni. Offro opportunità reali, con la stessa serietà con cui ho costruito la mia carriera."
                </blockquote>
                <cite className="text-lg text-accent font-semibold">— Gaby Mudingayi</cite>
              </div>
            </div>
            
            <div className="mt-8">
              <Button 
                size="lg" 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary hover:bg-primary/90 h-11 rounded-md btn-exclusive px-8 py-4 text-lg font-bold transition-smooth animate-pulse-glow text-[rgb(0,0,0)]" 
                onClick={() => window.open('https://calendly.com/gabymudingayiconsulting', '_blank')}
              >
                🚀 Prenota la tua consulenza con Gaby ora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="aurora"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 scroll-reveal-up">
            <Badge className="mb-4 badge-premium text-black font-bold">Domande Frequenti</Badge>
            <h2 className="text-4xl font-bold mb-4 title-section text-glow">Tutto quello che devi sapere</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Le risposte alle domande più comuni sui miei <span className="text-accent font-semibold">servizi</span>
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            <div className="scroll-reveal-up stagger-1">
              <details className="group bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 morph-shape">
                <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold">Come funziona la valutazione professionale?</h3>
                  <div className="text-accent group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-muted-foreground">
                  La valutazione include un'analisi completa delle tue capacità tecniche, tattiche e mentali. Riceverai un report dettagliato con consigli personalizzati per il tuo sviluppo.
                </div>
              </details>
            </div>

            <div className="scroll-reveal-up stagger-2">
              <details className="group bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 morph-shape">
                <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold">Che garanzie ho sui provini organizzati?</h3>
                  <div className="text-accent group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-muted-foreground">
                  Organizzo provini reali con club professionali. Ti preparo tecnicamente e mentalmente, ma il risultato dipende dalle tue prestazioni durante il provino.
                </div>
              </details>
            </div>

            <div className="scroll-reveal-up stagger-3">
              <details className="group bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 morph-shape">
                <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold">Il programma Elite Player è adatto a tutte le età?</h3>
                  <div className="text-accent group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-muted-foreground">
                  Il programma è pensato per giovani dai 14 ai 22 anni con reali ambizioni professionistiche. Ogni percorso viene personalizzato in base all'età e al livello.
                </div>
              </details>
            </div>

            <div className="scroll-reveal-up stagger-4">
              <details className="group bg-card/80 backdrop-blur-sm rounded-xl border border-primary/20 morph-shape">
                <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold">Posso avere un rimborso se non sono soddisfatto?</h3>
                  <div className="text-accent group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-muted-foreground">
                  Offro una garanzia di soddisfazione sui miei servizi. Se non sei completamente soddisfatto della valutazione, discuteremo insieme una soluzione.
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-black text-white py-12 border-t border-primary/20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">Gaby Mudingayi Consulting</h3>
              <p className="text-gray-300 mb-4">
                Ex giocatore di Serie A, oggi consulente sportivo e mentore per giovani talenti.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </div>
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Servizi</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Valutazione Professionale</li>
                <li>Provini in Club</li>
                <li>Allenamenti Personalizzati</li>
                <li>Programma Elite Player</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contatti</h4>
              <div className="space-y-2 text-gray-300">
                <p>Email: info@gabymudingayi.com</p>
                
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p className="text-sm mb-4">
              I servizi proposti sono di consulenza e accompagnamento professionale. Non garantiscono contratti sportivi o ingaggi diretti, ma offrono reali opportunità di visibilità e crescita.
            </p>
            <p>&copy; 2026 Gaby Mudingayi Consulting. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;